import React from 'react';
import { IMAGES } from '../constants/images';
import CTA from '../components/CTA';
import useIsMobile from '../utils/useIsMobile';
import {
  FiZap, FiShield, FiHeart, FiGlobe,
  FiUsers, FiTrendingUp, FiWifi, FiAward,
} from 'react-icons/fi';

const DotGrid = () => (
  <div aria-hidden style={{
    position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
  }} />
);

const StatBubble = ({ value, label }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: 'rgba(255,255,255,0.09)', backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.18)', borderRadius: 16, padding: '16px 22px',
  }}>
    <span style={{ fontSize: '1.7rem', fontWeight: 900, color: '#fff', lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', marginTop: 5, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{label}</span>
  </div>
);

const ValueCard = ({ icon: Icon, color, bg, title, desc }) => (
  <div style={{
    background: '#fff', borderRadius: 20, padding: '24px 20px',
    border: '1.5px solid #e8edf5', boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
    transition: 'transform 0.22s, box-shadow 0.22s',
  }}
  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${color}22`; }}
  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)'; }}
  >
    <div style={{ width: 46, height: 46, borderRadius: 13, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
      <Icon style={{ width: 19, height: 19, color }} />
    </div>
    <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.97rem', margin: '0 0 7px' }}>{title}</p>
    <p style={{ color: '#64748b', fontSize: '0.86rem', lineHeight: 1.7, margin: 0 }}>{desc}</p>
  </div>
);

const AboutPage = () => {
  const isMobile = useIsMobile(768);
  const isTablet = useIsMobile(1100);

  const values = [
    { icon: FiZap,        color: '#3b82f6', bg: '#eff6ff', title: 'Reliability',    desc: 'We focus on reliable connectivity — because staying online matters for modern life.' },
    { icon: FiTrendingUp, color: '#8b5cf6', bg: '#f5f3ff', title: 'Innovation',     desc: 'Helping customers reach modern internet and wireless options that fit their speed and budget needs.' },
    { icon: FiHeart,      color: '#ec4899', bg: '#fdf2f8', title: 'Customer First', desc: 'Every decision starts with one question: how does this benefit our customers?' },
    { icon: FiGlobe,      color: '#10b981', bg: '#ecfdf5', title: 'Community',      desc: 'Bridging the digital divide — affordable internet for underserved areas.' },
  ];

  const agenda = [
    { icon: FiUsers,     color: '#3b82f6', bg: '#eff6ff', title: 'Customer-Centric Solutions',   desc: 'Services tailored to the unique needs of homes and businesses.' },
    { icon: FiZap,       color: '#8b5cf6', bg: '#f5f3ff', title: 'Innovation & Technology',      desc: 'Adopting the latest tech to improve performance and experience.' },
    { icon: FiWifi,      color: '#0ea5e9', bg: '#f0f9ff', title: 'Reliable Connectivity',        desc: 'Consistent, secure Internet, Wi-Fi, Cable TV, and Wireless services.' },
    { icon: FiGlobe,     color: '#10b981', bg: '#ecfdf5', title: 'Sustainability',               desc: 'Ethical operations, community support, and reduced environmental impact.' },
    { icon: FiAward,     color: '#f59e0b', bg: '#fffbeb', title: 'Growth & Guidance',            desc: 'Expert guidance through plan selection, enrollment, and account setup as your needs grow.' },
  ];

  const responsibility = [
    { icon: FiHeart,  color: '#ec4899', bg: '#fdf2f8', title: 'Customer Commitment',       desc: 'Responsive sales and enrollment help so you can order the right plan with confidence.' },
    { icon: FiShield, color: '#10b981', bg: '#ecfdf5', title: 'Data Security & Privacy',   desc: 'Protected personal and business information using secure networks and strong practices.' },
    { icon: FiGlobe,  color: '#3b82f6', bg: '#eff6ff', title: 'Sustainable Practices',     desc: 'Energy-efficient operations and eco-friendly technologies to reduce impact.' },
    { icon: FiUsers,  color: '#8b5cf6', bg: '#f5f3ff', title: 'Community Engagement',      desc: 'Supporting local initiatives and digital literacy for stronger connected communities.' },
  ];

  return (
    <main style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: '#f8f9fc' }}>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: isMobile ? 'clamp(320px, 75vh, 420px)' : 'clamp(440px, 60vh, 540px)', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <img src={IMAGES.heroAbout} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.src = IMAGES.hero?.[0] ?? ''; }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(10,20,60,0.88) 0%, rgba(10,20,60,0.62) 55%, rgba(10,20,60,0.28) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,60,0.5) 0%, transparent 40%)' }} />
        <DotGrid />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: isMobile ? 'clamp(48px, 10vw, 64px) var(--cc-px) clamp(36px, 8vw, 48px)' : 'clamp(72px, 8vw, 100px) var(--cc-px) 70px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999,
            padding: '6px 18px', marginBottom: 22,
          }}>
            <FiGlobe style={{ color: '#60a5fa', width: 14, height: 14 }} />
            <span style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Our Story</span>
          </div>

          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 6rem)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            Connecting Communities,{isMobile ? ' ' : <br />}
            <span style={{ color: '#60a5fa' }}>One Home at a Time</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: isMobile ? '0.97rem' : '1.18rem', maxWidth: 680, margin: '0 auto 36px', lineHeight: 1.85 }}>
            InternetTVService — a DBA of CyberCloud LLC — is an independent Authorized Dealer for Internet and TV services. We help homes and small businesses compare and enroll in Internet, Cable TV, Wi‑Fi, and Wireless plans from licensed providers, with upfront quotes, clear guidance, and responsive sales & enrollment help for customers across the USA.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            <StatBubble value="Any Internet / Cable" label="Options" />
            <StatBubble value="All USA" label="Service Region" />
            <StatBubble value="Clear" label="Pricing Focus" />
            <StatBubble value="Sales" label="Enrollment Help" />
          </div>
        </div>
      </section>

      {/* ── WHO WE ARE ───────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(32px, 6vw, 48px) var(--cc-px)' : 'clamp(56px, 6vw, 90px) var(--cc-px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: isMobile ? 36 : 48, alignItems: 'center', maxWidth: 'min(100%, var(--content-max))', margin: '0 auto' }}>

          {/* Image card */}
          <div style={{ position: 'relative', paddingTop: isMobile ? 16 : 0 }}>
            <div style={{ borderRadius: 22, overflow: 'hidden', height: isMobile ? 260 : isTablet ? 320 : 400, position: 'relative', boxShadow: '0 24px 64px rgba(0,0,0,0.14)' }}>
              <img src={IMAGES.aboutPage} alt="Illustration representing home and business connectivity" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,60,0.75) 0%, transparent 55%)' }} />
              <div style={{ position: 'absolute', bottom: 22, left: 22, right: 22 }}>
                <p style={{ color: '#fff', fontWeight: 800, fontSize: isMobile ? '1rem' : '1.2rem', margin: '0 0 5px' }}>Who We Are</p>
                <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: isMobile ? '0.87rem' : '1rem', lineHeight: 1.75, margin: 0 }}>
                  As an Authorized Dealer, we help homes and businesses compare, enroll in, and set up services from licensed providers.
                </p>
              </div>
            </div>
            {/* Floating badge — factual business location */}
            <div style={{
              position: 'absolute', top: isMobile ? 4 : isTablet ? 4 : -18, right: isMobile ? 8 : isTablet ? 8 : -18,
              background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
              borderRadius: 14, padding: '12px 18px',
              boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
              color: '#fff', textAlign: 'center',
            }}>
              <p style={{ fontWeight: 900, fontSize: '1.05rem', margin: 0, lineHeight: 1 }}>Herndon</p>
              <p style={{ fontSize: '0.68rem', margin: '3px 0 0', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Virginia, USA</p>
            </div>
          </div>

          {/* Text content */}
          <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 12 }}>Our Mission</span>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 4rem)', fontWeight: 900, color: '#0f172a', lineHeight: 1.15, letterSpacing: '-0.02em', margin: '0 0 18px' }}>
              Trusted Provider Partners
            </h2>
            <p style={{ color: '#64748b', lineHeight: 1.8, margin: '0 0 16px', fontSize: '0.95rem' }}>
              Operated by CyberCloud LLC (DBA InternetTVService) from Herndon, Virginia, we work with established network providers as an Authorized Dealer and recommend plans based on your location, speed needs, and budget. No inflated claims—just practical options that fit.
            </p>
            <p style={{ color: '#64748b', lineHeight: 1.8, margin: '0 0 32px', fontSize: '0.95rem' }}>
              We serve homes and businesses across the USA. Availability depends on your specific address and third-party provider coverage networks — contact us to confirm what's available at your location, and we'll guide you through enrollment and installation setup.
            </p>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ───────────────────────────────────────────────── */}
      <section style={{ background: '#f1f5f9', padding: isMobile ? 'clamp(32px, 6vw, 48px) var(--cc-px)' : 'clamp(56px, 6vw, 90px) var(--cc-px)' }}>
        <div style={{ maxWidth: 'min(100%, var(--content-max))', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 10 }}>What Drives Us</span>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 4rem)', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Our Core Values</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 16 }}>
            {values.map((v, i) => <ValueCard key={i} {...v} />)}
          </div>
        </div>
      </section>

      {/* ── VISION ───────────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(32px, 6vw, 48px) var(--cc-px)' : 'clamp(56px, 6vw, 90px) var(--cc-px)' }}>
        <div style={{ maxWidth: 'min(100%, var(--content-max))', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: isMobile ? 24 : 32, alignItems: 'center' }}>

            {/* Dark card */}
            <div style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
              borderRadius: 22, padding: isMobile ? 'clamp(28px, 6vw, 36px) var(--cc-px)' : '44px 36px',
              position: 'relative', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(30,58,138,0.25)',
            }}>
              <div aria-hidden style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', border: '44px solid rgba(255,255,255,0.05)' }} />
              <div aria-hidden style={{ position: 'absolute', bottom: -80, left: 60, width: 160, height: 160, borderRadius: '50%', border: '30px solid rgba(96,165,250,0.07)' }} />
              <FiAward style={{ width: 26, height: 26, color: '#60a5fa', marginBottom: 18 }} />
              <h2 style={{ color: '#fff', fontSize: isMobile ? '1.5rem' : '1.8rem', fontWeight: 900, margin: '0 0 14px', letterSpacing: '-0.02em', lineHeight: 1.15 }}>Our Vision</h2>
              <p style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8, margin: '0 0 14px', fontSize: '0.93rem' }}>
                To be a trusted, helpful connectivity partner — making it easy for customers to compare and enroll in fast, reliable, and secure services from licensed providers.
              </p>
              <p style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8, margin: 0, fontSize: '0.93rem' }}>
                A connected world where technology enhances productivity, entertainment, and everyday life — upholding quality, reliability, and responsibility at every step.
              </p>
            </div>

            {/* Responsibility grid */}
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 10 }}>Our Commitment</span>
              <h2 style={{ fontSize: 'clamp(1.4rem, 2.5vw, 3.5rem)', fontWeight: 900, color: '#0f172a', margin: '0 0 24px', letterSpacing: '-0.02em' }}>Responsibility</h2>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                {responsibility.map((r, i) => {
                  const Icon = r.icon;
                  return (
                    <div key={i} style={{
                      background: '#fff', borderRadius: 15, padding: '18px',
                      border: '1.5px solid #e8edf5', boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: r.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                        <Icon style={{ width: 15, height: 15, color: r.color }} />
                      </div>
                      <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.87rem', margin: '0 0 5px' }}>{r.title}</p>
                      <p style={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.65, margin: 0 }}>{r.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AGENDA ───────────────────────────────────────────────────── */}
      <section style={{ background: '#f1f5f9', padding: isMobile ? 'clamp(32px, 6vw, 48px) var(--cc-px)' : 'clamp(56px, 6vw, 90px) var(--cc-px)' }}>
        <div style={{ maxWidth: 'min(100%, var(--content-max))', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 10 }}>Priorities</span>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 4rem)', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Our Agenda</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 16 }}>
            {agenda.map((a, i) => <ValueCard key={i} {...a} />)}
          </div>
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default AboutPage;
